import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { MentorListItem } from "./types";

interface Options {
  app: string | null;
  specialty?: string | null;
}

/**
 * List approved, active, badge-visible mentors for a given app, joined with
 * their public profile (display_name, avatar_url, username).
 */
export const useMentors = ({ app, specialty }: Options) => {
  const [mentors, setMentors] = useState<MentorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!app) {
      setMentors([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    let q = supabase
      .from("mentor_profiles_public" as any)
      .select("*")
      .eq("app", app as any);

    if (specialty) q = q.contains("specialties", [specialty]);

    const { data: rows, error: e1 } = await q.limit(60);
    if (e1) {
      setError(e1.message);
      setLoading(false);
      return;
    }

    const userIds = (rows ?? []).map((r: any) => r.user_id);
    let profilesById = new Map<string, any>();
    if (userIds.length) {
      const { data: profs } = await supabase
        .from("public_profiles")
        .select("id, display_name, avatar_url, username")
        .in("id", userIds);
      profilesById = new Map((profs ?? []).map((p: any) => [p.id, p]));
    }

    const merged: MentorListItem[] = (rows ?? []).map((r: any) => {
      const p = profilesById.get(r.user_id);
      return {
        ...r,
        display_name: p?.display_name ?? null,
        avatar_url: p?.avatar_url ?? null,
        username: p?.username ?? null,
      } as MentorListItem;
    });

    setMentors(merged);
    setLoading(false);
  }, [app, specialty]);

  useEffect(() => {
    void load();
  }, [load]);

  return { mentors, loading, error, reload: load };
};
